import React, { useEffect } from 'react';
import { Container, FormikWrapper } from '@uncinc/uncinc-react-kitchen-sink';
import { getOneTimeLoginParams, oneTimeLogin, resetPassword, CoreLink } from '@uncinc/react-drupal-core';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { Button, PasswordField } from '../../../components';
import { MINIMUM_PASSWORD_CHARACTER_COUNT, DEFAULT_LANGUAGE } from '../../../config/constants';
import { ROUTE_PASSWORD_REQUEST } from '../../../config/routes';
import LogoImageVNL from '../../../assets/images/VNL_logo_clean.svg';

const UserResetPassword = ({ path }) => {
  const PAGE_TITLE = 'Reset wachtwoord | VeiligheidNL';
  const dispatch = useDispatch();
  const history = useHistory();
  const { uid, timestamp, hash } = getOneTimeLoginParams(path);

  const validOneTimeLogin = useSelector((state) => state.getIn(['login', 'validOneTimeLogin'], null));
  const passResetToken = useSelector((state) => state.getIn(['login', 'passResetToken'], null));
  const requestStatus = useSelector((state) => state.getIn(['ui', 'user', 'operations', 'reset_password', 'status'], null));
  const { t } = useTranslation();

  // Validate login tokens.
  useEffect(() => {
    dispatch(oneTimeLogin(uid, timestamp, hash));
  }, [dispatch, uid, timestamp, hash]);

  // Redirect to home if login tokens are invalid.
  useEffect(() => {
    if (validOneTimeLogin === false) {
      history.push(ROUTE_PASSWORD_REQUEST, { invalid_token: 'reset_password' });
    }
  }, [validOneTimeLogin, history]);

  const onSubmit = (password) => {
    dispatch(resetPassword(password, passResetToken));
  };

  useEffect(() => {
    if (requestStatus === 'UI_OPERATION_STATUS_SUCCESS') {
      history.push('/success/resetpassword');
    }
  });

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <div className="FormPage PageResetPassword PageMinimal" id="main">
        <Container small>
          {requestStatus !== 'UI_OPERATION_STATUS_SUCCESS' && (
            <div>
              <div className="PageMinimal__top">
                <CoreLink className="Logo" href="https://www.veiligheid.nl">
                  <img src={LogoImageVNL} alt="VeiligheidNL" />
                </CoreLink>
                <h1>{t('reset_password.title')}</h1>
              </div>
              <div className="PageMinimal__content">
                <FormikWrapper
                  initialValues={{ password: '', password_confirm: '' }}
                  validationSchema={yup.object().shape({
                    password: yup
                      .string()
                      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t('password.password.minimum', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }))
                      .required(t('password.password.required')),
                    password_confirm: yup
                      .string()
                      .required(t('password.password_confirm.required'))
                      .oneOf([yup.ref('password'), null], t('password.password_confirm.no_match')),
                  })}
                  onSubmit={(values, { setSubmitting }) => {
                    onSubmit(values.password);
                    setSubmitting(false);
                  }}
                >
                  <PasswordField
                    fieldName="password"
                    title={t('password.password.label_reset')}
                    required
                  />
                  <PasswordField
                    fieldName="password_confirm"
                    title={t('password.password_confirm.label')}
                    required
                  />
                  <Button htmlType="submit">{t('reset_password.submit')}</Button>
                </FormikWrapper>
              </div>
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default UserResetPassword;
